import React, { useEffect, useMemo, useState } from "react";
import moment from "moment-timezone";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { drawerVariants } from "../../assets/framer/styles";
import {
  setTimezone,
  setStartDate,
  setEndDate,
  setHourOfDonation,
  setInterval
} from "../../store/slices/donation";
import {
  navbarVariants,
  logoVariants,
  dropdownVariants
} from "../../assets/framer/styles";
import "./index.scss";
import logo from "../../assets/images/Auto Giving Final Logo.png";
import logoOGN from "../../assets/images/logo-ogn.png";
import CustomDropdown from "../../components/CustomDropdown";
import { FaBars, FaTimes } from "react-icons/fa";

// Only the calendars we support here
import {
  fridays2025,
  dhul_hijjah_hijri_2026,
  dhul_hijjah_lunar_2026,
  ramadan_hijri_2026,
  ramadan_lunar_2026,
  ramadan_zakat_hijri_2026,
  ramadan_zakat_lunar_2026
} from "../../utils/calendarTemplates";
import { useTranslation } from "react-i18next";
import {
  getCanonicalTemplateKey,
  isDhulHijjahTemplateKey,
  isRamadanTemplateKey,
  isZakatTemplateKey
} from "../../utils/templateUtils";

const Navbar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const { i18n } = useTranslation();

  const [selectedTimezoneName, setSelectedTimezoneName] = useState(null);
  const [selectedCalendar, setSelectedCalendar] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const searchParams = new URLSearchParams(location.search);
  const template = searchParams.get("template");
  const templateKey = getCanonicalTemplateKey(template);
  const ref = searchParams.get("ref");

  // Calendars grouped to constrain choices based on current flow
  const dhulHijjahCalendars = [dhul_hijjah_hijri_2026, dhul_hijjah_lunar_2026];
  const ramadanCalendars = [ramadan_hijri_2026, ramadan_lunar_2026];
  const zakatCalendars = [ramadan_zakat_hijri_2026, ramadan_zakat_lunar_2026];
  const fridaysCalendars = [fridays2025];

  const isZakatTemplate = isZakatTemplateKey(templateKey);
  const isRamadanTemplate = isRamadanTemplateKey(templateKey);
  const isDhulHijjahTemplate = isDhulHijjahTemplateKey(templateKey);

  // Constrain options: zakat templates only see zakat calendars; other Ramadan flows see non-zakat Ramadan; default shows everything
  const availableCalendars = (
    isZakatTemplate
      ? zakatCalendars
      : isDhulHijjahTemplate
      ? dhulHijjahCalendars
      : isRamadanTemplate
      ? ramadanCalendars
      : [...dhulHijjahCalendars, ...ramadanCalendars, ...zakatCalendars, ...fridaysCalendars]
  ).map((item) => ({
    ...item,
    key: item.key || item.label.toLowerCase().replace(/\s+/g, "_")
  }));

  // Dropdown options (label for display, key for value)
  const calendarOptions = availableCalendars.map((item) => ({
    label: item.label,
    value: item.key,
    startDate: item.startDate,
    endDate: item.endDate,
    hourOfDonation: item.hourOfDonation,
    interval: item.interval
  }));

  // Determine if the current template is a "10-night" calendar
  // const isTenNightCalendar = template === "Hijri10" || template === "Lunar10";

  // Set timezone options (same as before)
  const timezones = useMemo(() => {
    return moment.tz.names().map((tz) => {
      const offset = moment.tz(tz).utcOffset() / 60;
      const sign = offset >= 0 ? "+" : "";
      const gmtOffset = `(GMT${sign}${offset})`;
      return {
        label: `${tz} ${gmtOffset}`,
        value: tz,
        offset: gmtOffset
      };
    });
  }, []);

  // Language dropdown options (EN, FR)
  const languageOptions = [
    { label: "EN", value: "en" },
    { label: "FR", value: "fr" }
  ];

  // Find which option matches the current i18n language
  const currentLangOption =
    languageOptions.find((opt) => opt.value === i18n.language) ||
    languageOptions[0];

  // Called when user picks a new language
  const handleLanguageChange = (selectedOption) => {
    i18n.changeLanguage(selectedOption.value);
  };

  // On initial or URL change, set the selectedCalendar based on template.
  // Note: Only auto-select if the current template is one of the calendar options.
  useEffect(() => {
    if (!template) return;
    const foundCalendar =
      calendarOptions.find((calendar) => calendar.value === templateKey) ||
      calendarOptions.find((calendar) => calendar.label.toLowerCase() === templateKey) ||
      calendarOptions.find((calendar) => calendar.label.toLowerCase().replace(/\s+/g, "_") === templateKey);
    if (foundCalendar) {
      setSelectedCalendar(foundCalendar.value);
    } else if (calendarOptions.length) {
      if (isDhulHijjahTemplate || isRamadanTemplate || isZakatTemplate) {
        return;
      }
      const fallback = calendarOptions[0];
      setSelectedCalendar(fallback.value);
      // Keep URL in sync if we need to coerce into allowed set
      const currentParams = new URLSearchParams(location.search);
      currentParams.set("template", fallback.value);
      navigate(`/donate?${currentParams.toString()}`, { replace: true });
    }
  }, [
    template,
    templateKey,
    calendarOptions,
    isDhulHijjahTemplate,
    isRamadanTemplate,
    isZakatTemplate,
    location.search,
    navigate
  ]);

  // Set timezone and language once
  useEffect(() => {
    const detectedTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    dispatch(setTimezone(detectedTimeZone));
    setSelectedTimezoneName(detectedTimeZone);
  }, [dispatch]);

  const handleTimezoneChange = (selectedOption) => {
    dispatch(setTimezone(selectedOption.value));
    setSelectedTimezoneName(selectedOption.value);
  };

  // Update Redux state when selectedCalendar changes
  useEffect(() => {
    const currentCalendar = calendarOptions.find((c) => c.value === selectedCalendar);
    if (currentCalendar) {
      dispatch(setStartDate(currentCalendar.startDate));
      dispatch(setEndDate(currentCalendar.endDate));
      dispatch(setHourOfDonation(currentCalendar.hourOfDonation));
      dispatch(setInterval(currentCalendar.interval));
    }
  }, [selectedCalendar, calendarOptions, dispatch]);

  const handleCalendarChange = (selectedOption) => {
    // Update Redux state for calendar values
    dispatch(setStartDate(selectedOption.startDate));
    dispatch(setEndDate(selectedOption.endDate));
    dispatch(setHourOfDonation(selectedOption.hourOfDonation));
    dispatch(setInterval(selectedOption.interval));
    setSelectedCalendar(selectedOption.value);

    // Update URL query parameters
    const currentParams = new URLSearchParams(location.search);
    currentParams.set("template", selectedOption.value);
    navigate(`/donate?${currentParams.toString()}`);
  };

  const handleLogoClick = () => {
    navigate("/home");
  };

  // Determine if we should show the "Select Timezone" dropdown (only on /donate)
  const shouldShowTimezoneDropdown = location.pathname === "/donate";

  // Hide the "Select Calendar" dropdown for now to reduce confusion.
  const shouldShowCalendarDropdown = false;

  return (
    <div>
      <motion.div
        className="navbar-container"
        variants={navbarVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.img
          className="logo"
          src={ref && ref.toLowerCase().includes("ogn") ? logoOGN : logo}
          alt="logo"
          onClick={handleLogoClick}
          variants={logoVariants}
          style={{
            ...(ref &&
              ref.toLowerCase().includes("ogn") && {
              maxWidth: "220px",
              paddingTop: "1rem",
              paddingBottom: "1rem"
            })
          }}
          whileHover="hover"
        />
        <motion.div className="settings-container" variants={navbarVariants}>
          {shouldShowTimezoneDropdown && (
            <motion.div variants={dropdownVariants}>
              <CustomDropdown
                options={timezones}
                selectedOption={timezones.find(
                  (tz) => tz.value === selectedTimezoneName
                )}
                onChange={handleTimezoneChange}
                placeholder="Select Timezone"
                minWidth={270}
              />
            </motion.div>
          )}
          {shouldShowCalendarDropdown && (
            <motion.div variants={dropdownVariants}>
              <CustomDropdown
                options={calendarOptions}
                selectedOption={calendarOptions.find(
                  (calendar) => calendar.value === selectedCalendar
                )}
                onChange={handleCalendarChange}
                placeholder="Select Calendar"
                formatOptionLabel={(option) => option.label.replace(/10$/, "")}
              />
            </motion.div>
          )}
          <CustomDropdown
            options={languageOptions}
            selectedOption={currentLangOption}
            onChange={handleLanguageChange}
            placeholder="Select Language"
            minWidth={120}
          />
        </motion.div>
        <div className="hamburger-menu" onClick={() => setDrawerOpen(true)}>
          <FaBars size={24} />
        </div>
      </motion.div>
      <AnimatePresence>
        {drawerOpen && (
          <motion.div
            className="drawer"
            variants={drawerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <button
              className="close-button"
              onClick={() => setDrawerOpen(false)}
            >
              <FaTimes size={24} />
            </button>
            <div className="drawer-content">
              <CustomDropdown
                options={timezones}
                selectedOption={timezones.find(
                  (tz) => tz.value === selectedTimezoneName
                )}
                onChange={handleTimezoneChange}
                placeholder="Select Timezone"
              />
              {shouldShowCalendarDropdown && (
                <motion.div variants={dropdownVariants}>
                  <CustomDropdown
                    options={calendarOptions}
                    selectedOption={calendarOptions.find(
                      (calendar) => calendar.value === selectedCalendar
                    )}
                    onChange={handleCalendarChange}
                    placeholder="Select Calendar"
                  />
                </motion.div>
              )}
              <CustomDropdown
                options={languageOptions}
                selectedOption={currentLangOption}
                onChange={handleLanguageChange}
                placeholder="Select Language"
                minWidth={120}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Navbar;
